"use client";

import { useState } from "react";
import { Eyebrow } from "@/components/ui/Eyebrow";
import { Serif } from "@/components/ui/Serif";
import { Icon } from "@/components/ui/Icon";
import { DEFAULT_PARTNER_PRICING } from "@/lib/paiement/partner-pricing";

/** « 50 € » ou « 45,50 € » : le tarif s'affiche sans décimales quand il est rond. */
function formatTarif(cents: number): string {
  const euros = cents / 100;
  return `${Number.isInteger(euros) ? euros : euros.toFixed(2).replace(".", ",")} €`;
}

type FaqItem = {
  n: string;
  q: string;
  /** Réponse en HTML léger (strong/em uniquement, contenu de confiance). */
  a: string;
  tags: string[];
};

/**
 * Le lieu du rendez-vous n'est PAS le même selon le partenaire : Cap 3000 a un
 * espace nommé et public (l'Appartement Corso), alors que les partenaires
 * entreprise reçoivent leurs collaborateurs dans un espace communiqué à la
 * réservation. Annoncer « Cap 3000, à Nice » à une cliente L'Oréal était faux.
 */
const CAP3000_SLUG = "cap3000";

function faqItems(partnerSlug?: string, prestationCents = DEFAULT_PARTNER_PRICING.prestationCents): FaqItem[] {
  const isCap3000 = partnerSlug === CAP3000_SLUG;
  const tarif = formatTarif(prestationCents);

  const accueil = isCap3000
    ? "vous êtes accueilli·e à l'Appartement Corso"
    : "vous êtes accueilli·e dans l'espace dédié de votre partenaire";

  const lieu: FaqItem = isCap3000
    ? {
        n: "04",
        q: "Où se déroule le rendez-vous ?",
        a: "À <strong>l'Appartement Corso</strong>, au sein du centre Cap 3000, à Nice. Il vous suffit de vous présenter à l'accueil de l'Appartement : votre Personal Shopper vous y reçoit dans un espace dédié, intime et confortable, où le temps prend une autre cadence.",
        tags: ["Cap 3000", "Nice"],
      }
    : {
        n: "04",
        q: "Où se déroule le rendez-vous ?",
        a: "Dans un <strong>espace dédié</strong>, au sein du lieu de votre partenaire. L'adresse exacte et les modalités d'accès vous sont communiquées au moment de la réservation, puis rappelées dans votre confirmation de rendez-vous. Vous y êtes reçu·e dans un cadre intime et confortable, où le temps prend une autre cadence.",
        tags: ["Espace dédié", "Sur réservation"],
      };

  return FAQ_ITEMS.map((item) => {
    if (item.n === "04") return lieu;
    if (item.n === "02") return { ...item, a: item.a.replace("vous êtes accueilli·e à l'Appartement Corso", accueil) };
    if (item.n === "06") {
      return { ...item, a: item.a.replace("{{tarif}}", tarif), tags: item.tags.map((t) => t.replace("{{tarif}}", tarif)) };
    }
    return item;
  });
}

const FAQ_ITEMS: FaqItem[] = [
  {
    n: "01",
    q: "Qu'est-ce que Fine Your Trend, précisément ?",
    a: "Un service de personal shopping <strong>hybride</strong>, à la croisée du digital et du physique. Vous préparez votre visite en quelques minutes sur notre application ; vous vivez ensuite un véritable rendez-vous mode auprès d'un Personal Shopper qui a sélectionné, en amont, des pièces pensées pour vous. <em>Ni livraison, ni algorithme</em> — une rencontre, et une garde-robe à votre image.",
    tags: ["Service hybride", "Sur-mesure"],
  },
  {
    n: "02",
    q: "Comment se déroule l'expérience, concrètement ?",
    a: "Tout débute sur l'application Fine Your Trend, où vous nous confiez votre style, vos envies et votre budget. Votre Personal Shopper compose alors une sélection multimarques pensée pour vous. <strong>Le jour du rendez-vous</strong>, vous êtes accueilli·e à l'Appartement Corso : vous essayez, vous choisissez, vous repartez avec ce qui vous ressemble.",
    tags: ["Trois temps", "Multimarques"],
  },
  {
    n: "03",
    q: "Qui sera mon Personal Shopper ?",
    a: "Un·e expert·e mode passionné·e, formé·e à un cérémonial de conseil <em>haut de gamme</em>. Il ou elle allie acuité stylistique, sens du service et connaissance fine des marques pour vous proposer une sélection juste. <strong>Bien davantage qu'un vendeur : un véritable conseiller</strong>, qui vous est entièrement dédié pendant la durée de votre rendez-vous.",
    tags: ["Conseiller dédié"],
  },
  {
    n: "04",
    q: "Où se tient le rendez-vous ?",
    a: "À <strong>l'Appartement Corso</strong>, au sein du centre Cap 3000, à Nice. Il vous suffit de vous présenter à l'accueil de l'Appartement : votre Personal Shopper vous y reçoit dans un espace dédié, intime et confortable, où le temps prend une autre cadence.",
    tags: ["Cap 3000", "Nice"],
  },
  {
    n: "05",
    q: "Comment réserver un rendez-vous ?",
    a: "Directement depuis l'application Fine Your Trend, en quelques instants. Vous choisissez le créneau qui vous convient, vous renseignez votre profil style, et votre Personal Shopper dispose de tout le nécessaire pour préparer votre sélection <em>avant même votre arrivée</em>.",
    tags: ["Application FYT"],
  },
  {
    n: "06",
    q: "Quel est le tarif du service ?",
    a: "Le service est proposé à <strong>{{tarif}}</strong>. Vous ne réglez ensuite que les pièces que vous décidez de garder, <em>aux prix publics pratiqués par les marques</em> — sans supplément, sans marge ajoutée.",
    tags: ["{{tarif}}", "Prix publics"],
  },
  {
    n: "07",
    q: "Que comprend exactement la prestation ?",
    a: "Un rendez-vous avec un Personal Shopper dédié, une sélection préparée <strong>avant votre arrivée</strong> à partir de votre profil style, et l'essayage en boutique accompagné. Vous repartez avec les pièces que vous choisissez — et rien d'autre.",
    tags: ["Shopper dédié", "Sélection préparée"],
  },
  {
    n: "08",
    q: "Comment se règlent les achats ?",
    a: "Le règlement s'effectue sur place, à l'issue de votre rendez-vous, via l'application Fine Your Trend : <strong>simple, sécurisé, immédiat</strong>. Les prix pratiqués sont rigoureusement identiques à ceux des marques — <em>aucune marge ajoutée, aucun frais dissimulé</em>.",
    tags: ["Paiement in-app", "Prix publics"],
  },
  {
    n: "09",
    q: "Puis-je annuler ou déplacer mon rendez-vous ?",
    a: "Bien entendu. Vous pouvez modifier ou annuler votre rendez-vous <strong>jusqu'à 24 heures avant</strong> votre session, depuis l'application Fine Your Trend ou par téléphone. Et si un impératif survient le jour même, votre Personal Shopper saura s'adapter à votre emploi du temps : il suffit de l'en informer en amont.",
    tags: ["Souplesse", "Jusqu'à 24 h avant"],
  },
  {
    n: "10",
    q: "Mes données personnelles sont-elles protégées ?",
    a: "Oui, sans compromis. Les informations que vous partagez sur l'application (style, envies, budget) sont exclusivement destinées à votre Personal Shopper, dans le seul but de préparer votre sélection. <strong>Elles ne sont jamais transmises à des tiers.</strong> Fine Your Trend applique l'ensemble des standards <em>RGPD</em> en vigueur pour protéger votre vie privée.",
    tags: ["Conforme RGPD", "Confidentialité"],
  },
];

type FaqProps = {
  /** Slug du partenaire : adapte les réponses qui dépendent du lieu. */
  partnerSlug?: string;
  /** Tarif de la prestation de ce partenaire (centimes) ; 50 € par défaut. */
  prestationCents?: number;
};

export function Faq({ partnerSlug, prestationCents }: FaqProps) {
  const items = faqItems(partnerSlug, prestationCents);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (i: number) => setOpenIndex((cur) => (cur === i ? null : i));

  return (
    <section id="faq" className="border-t border-line bg-bg-warm">
      <div className="mx-auto w-full max-w-[920px] px-6 py-16 md:px-12 md:py-24">
      <Eyebrow className="mb-6 md:mb-7">Questions fréquentes</Eyebrow>

      <Serif
        as="h2"
        className="mb-5 max-w-[18ch] text-[32px] font-normal leading-[1.08] md:text-[52px]"
      >
        Avant de vous laisser <em className="not-italic text-gold-deep [font-style:italic]">guider</em>,
        quelques précisions.
      </Serif>

      <p className="max-w-[56ch] text-sm font-light leading-[1.65] text-body md:text-[16px]">
        Fine Your Trend est un service de personal shopping pensé comme une parenthèse —
        humaine, préparée, sans friction. Nous répondons ici aux questions que l'on nous
        pose le plus souvent, avec la même attention qu'en rendez-vous.
      </p>

      <div className="mt-10 flex items-center justify-between border-b border-line pb-4 md:mt-12">
        <div className="text-xs uppercase tracking-eyebrow text-muted">
          <strong className="font-semibold text-ink">{items.length}</strong> questions
        </div>
      </div>

      <ul className="list-none" role="list">
        {items.map((item, i) => {
          const open = openIndex === i;
          return (
            <li key={item.n} className="border-b border-line">
              <button
                type="button"
                aria-expanded={open}
                aria-controls={`faq-a-${i}`}
                id={`faq-q-${i}`}
                onClick={() => toggle(i)}
                className="grid w-full grid-cols-[28px_1fr_auto] items-baseline gap-3 py-6 text-left transition-colors hover:text-gold-deep md:grid-cols-[38px_1fr_auto] md:gap-4 md:py-7"
              >
                <span className="font-serif text-xs italic text-gold-deep md:text-sm">
                  {item.n}
                </span>
                <span className="font-serif text-[17px] font-normal leading-[1.32] text-ink md:text-[22px]">
                  {item.q}
                </span>
                <span
                  aria-hidden
                  className={`flex h-8 w-8 flex-shrink-0 items-center justify-center self-center rounded-full border transition-all duration-500 md:h-9 md:w-9 ${
                    open
                      ? "border-ink bg-ink text-bg"
                      : "border-line-strong text-body"
                  }`}
                >
                  <Icon
                    name="chevron-down"
                    size={14}
                    strokeWidth={1.4}
                    className={`transition-transform duration-500 ${open ? "rotate-180" : ""}`}
                  />
                </span>
              </button>

              <div
                id={`faq-a-${i}`}
                role="region"
                aria-labelledby={`faq-q-${i}`}
                className={`grid transition-all duration-500 ease-[cubic-bezier(.6,.05,.2,1)] ${
                  open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                }`}
              >
                <div className="overflow-hidden">
                  <div className="pb-7 pl-0 pr-0 md:pl-[54px]">
                    <p
                      className="faq-answer max-w-[60ch] text-[14.5px] font-light leading-[1.7] text-body md:text-[15.5px] md:leading-[1.72]"
                      dangerouslySetInnerHTML={{ __html: item.a }}
                    />
                    {item.tags.length > 0 && (
                      <div className="mt-5 flex flex-wrap gap-2.5">
                        {item.tags.map((t) => (
                          <span
                            key={t}
                            className="rounded-pill border border-gold-soft bg-gold-soft/30 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-gold-deep"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="mt-12 rounded-md border border-line bg-card px-6 py-8 text-center md:px-10 md:py-10">
        <Serif as="h3" className="mb-2.5 text-[22px] font-normal md:text-[26px]">
          Une question qui n'apparaît pas ici ?
        </Serif>
        <p className="mx-auto max-w-[52ch] text-[13.5px] font-light leading-[1.6] text-body">
          Notre conciergerie vous répond avec la même attention que votre Personal Shopper,
          sous vingt-quatre heures — depuis l'application Fine Your Trend ou par téléphone.
        </p>
      </div>
      </div>
    </section>
  );
}
